import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { TaskCalendarSyncRepository } from '../domain/task-calendar-sync.repository';
import {
  TaskCalendarCandidate,
  TaskCalendarDayTasks,
  TaskCalendarKind,
  TaskCalendarPlacement,
  TaskCalendarStatus
} from '../models/task-calendar.model';

interface PlacementRow {
  id: number;
  task_occurrence_id: number;
  calendar_date: string;
  axis_id: number;
  position: number;
}

interface OccurrenceRow {
  id: number;
  task_definition_id: number;
  task_kind: TaskCalendarKind;
  scheduled_for: string | null;
  original_scheduled_for: string | null;
  rollover_days: number;
  status: TaskCalendarStatus;
  completed_on: string | null;
  completed_at: string | null;
  closed_at: string | null;
  cancellation_reason: string | null;
}

interface DefinitionRow {
  id: number;
  title: string;
  description: string | null;
  priority: number | null;
  default_interval_days: number | null;
}

@Injectable()
export class SupabaseTaskCalendarSyncRepository extends TaskCalendarSyncRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async loadPlacements(startDate: string, endDate: string): Promise<TaskCalendarPlacement[]> {
    const { data: placements, error: placementsError } = await this.client
      .from('calendar_entries')
      .select('id,task_occurrence_id,calendar_date,axis_id,position')
      .eq('entry_kind', 'TASK')
      .is('deleted_at', null)
      .gte('calendar_date', startDate)
      .lte('calendar_date', endDate)
      .order('calendar_date')
      .order('axis_id')
      .order('position')
      .order('id');
    if (placementsError) throw placementsError;

    const placementRows = (placements ?? []) as PlacementRow[];
    if (!placementRows.length) return [];

    const occurrenceIds = placementRows.map(row => row.task_occurrence_id);
    const occurrences = await this.loadOccurrences(occurrenceIds);
    const definitions = await this.loadDefinitions(
      [...new Set(occurrences.map(row => row.task_definition_id))]
    );
    const occurrenceById = new Map(occurrences.map(row => [row.id, row]));
    const definitionById = new Map(definitions.map(row => [row.id, row]));

    return placementRows.flatMap(placement => {
      const occurrence = occurrenceById.get(placement.task_occurrence_id);
      if (!occurrence) return [];
      const definition = definitionById.get(occurrence.task_definition_id);
      if (!definition) return [];

      return [this.mapPlacement(placement, occurrence, definition)];
    });
  }

  async searchOpenOccurrences(query: string, routineDate?: string): Promise<TaskCalendarCandidate[]> {
    if (routineDate) {
      const { error: materializeError } = await this.client.rpc('materialize_routine_date', {
        p_date: routineDate
      });
      if (materializeError) throw materializeError;
    }

    const [taskResult, routineResult] = await Promise.all([
      this.client
        .from('task_occurrences')
        .select(`
          id,
          task_definition_id,
          task_kind,
          scheduled_for,
          original_scheduled_for,
          rollover_days,
          status,
          completed_on,
          completed_at,
          closed_at,
          cancellation_reason
        `)
        .eq('status', 'OPEN')
        .in('task_kind', ['TODO', 'RECURRENT'])
        .order('scheduled_for', { ascending: true, nullsFirst: false })
        .limit(200),
      routineDate
        ? this.client
            .from('task_occurrences')
            .select(`
              id,
              task_definition_id,
              task_kind,
              scheduled_for,
              original_scheduled_for,
              rollover_days,
              status,
              completed_on,
              completed_at,
              closed_at,
              cancellation_reason
            `)
            .eq('status', 'OPEN')
            .eq('task_kind', 'ROUTINE')
            .eq('scheduled_for', routineDate)
            .limit(200)
        : Promise.resolve({ data: [], error: null })
    ]);

    if (taskResult.error) throw taskResult.error;
    if (routineResult.error) throw routineResult.error;

    const occurrences = [
      ...((taskResult.data ?? []) as OccurrenceRow[]),
      ...((routineResult.data ?? []) as OccurrenceRow[])
    ];
    if (!occurrences.length) return [];

    const definitions = await this.loadDefinitions(
      [...new Set(occurrences.map(row => row.task_definition_id))]
    );
    const definitionById = new Map(definitions.map(row => [row.id, row]));

    const occurrenceIds = occurrences.map(row => row.id);
    const { data: placementData, error: placementError } = await this.client
      .from('calendar_entries')
      .select('id,task_occurrence_id,calendar_date,axis_id,position')
      .eq('entry_kind', 'TASK')
      .is('deleted_at', null)
      .in('task_occurrence_id', occurrenceIds);
    if (placementError) throw placementError;

    const placementByOccurrence = new Map(
      ((placementData ?? []) as PlacementRow[]).map(row => [row.task_occurrence_id, row])
    );
    const normalizedQuery = query.trim().toLocaleLowerCase('fr');

    return occurrences
      .flatMap(occurrence => {
        const definition = definitionById.get(occurrence.task_definition_id);
        if (!definition) return [];

        const haystack = `${definition.title} ${definition.description ?? ''}`.toLocaleLowerCase('fr');
        if (normalizedQuery && !haystack.includes(normalizedQuery)) return [];

        const placement = placementByOccurrence.get(occurrence.id);
        return [{
          taskOccurrenceId: occurrence.id,
          taskDefinitionId: occurrence.task_definition_id,
          taskKind: occurrence.task_kind,
          title: definition.title,
          description: definition.description,
          priority: definition.priority,
          scheduledFor: occurrence.scheduled_for,
          originalScheduledFor: occurrence.original_scheduled_for,
          rolloverDays: occurrence.rollover_days,
          status: 'OPEN' as const,
          intervalDays: definition.default_interval_days,
          placement: placement ? {
            id: placement.id,
            calendarDate: placement.calendar_date,
            axisId: placement.axis_id,
            position: placement.position
          } : null
        } satisfies TaskCalendarCandidate];
      })
      .sort((left, right) => {
        if (left.taskKind !== right.taskKind) return left.taskKind.localeCompare(right.taskKind);
        if (!!left.placement !== !!right.placement) return left.placement ? 1 : -1;
        return left.title.localeCompare(right.title, 'fr');
      });
  }

  async loadDateTasks(targetDate: string): Promise<TaskCalendarDayTasks> {
    const { error: materializeError } = await this.client.rpc('materialize_routine_date', {
      p_date: targetDate
    });
    if (materializeError) throw materializeError;

    const [occurrencesResult, placementsResult] = await Promise.all([
      this.client
        .from('task_occurrences')
        .select(`
          id,
          task_definition_id,
          task_kind,
          scheduled_for,
          original_scheduled_for,
          rollover_days,
          status,
          completed_on,
          completed_at,
          closed_at,
          cancellation_reason
        `)
        .eq('scheduled_for', targetDate)
        .order('id')
        .limit(400),
      this.client
        .from('calendar_entries')
        .select('id,task_occurrence_id,calendar_date,axis_id,position')
        .eq('entry_kind', 'TASK')
        .is('deleted_at', null)
        .eq('calendar_date', targetDate)
        .order('axis_id')
        .order('position')
        .order('id')
    ]);

    if (occurrencesResult.error) throw occurrencesResult.error;
    if (placementsResult.error) throw placementsResult.error;

    const occurrences = (occurrencesResult.data ?? []) as OccurrenceRow[];
    const placementRows = (placementsResult.data ?? []) as PlacementRow[];
    if (!occurrences.length) return { placements: [], candidates: [] };

    const definitions = await this.loadDefinitions(
      [...new Set(occurrences.map(row => row.task_definition_id))]
    );
    const definitionById = new Map(definitions.map(row => [row.id, row]));
    const occurrenceById = new Map(occurrences.map(row => [row.id, row]));
    const placementByOccurrence = new Map(
      placementRows.map(row => [row.task_occurrence_id, row])
    );

    const placements = placementRows.flatMap(placement => {
      const occurrence = occurrenceById.get(placement.task_occurrence_id);
      if (!occurrence) return [];
      const definition = definitionById.get(occurrence.task_definition_id);
      if (!definition) return [];
      return [this.mapPlacement(placement, occurrence, definition)];
    });

    const candidates = occurrences
      .filter(occurrence => occurrence.status === 'OPEN' && !placementByOccurrence.has(occurrence.id))
      .flatMap(occurrence => {
        const definition = definitionById.get(occurrence.task_definition_id);
        if (!definition) return [];
        return [{
          taskOccurrenceId: occurrence.id,
          taskDefinitionId: occurrence.task_definition_id,
          taskKind: occurrence.task_kind,
          title: definition.title,
          description: definition.description,
          priority: definition.priority,
          scheduledFor: occurrence.scheduled_for,
          originalScheduledFor: occurrence.original_scheduled_for,
          rolloverDays: occurrence.rollover_days,
          status: 'OPEN' as const,
          intervalDays: definition.default_interval_days,
          placement: null
        } satisfies TaskCalendarCandidate];
      })
      .sort((left, right) => {
        if (left.taskKind !== right.taskKind) return left.taskKind.localeCompare(right.taskKind);
        return left.title.localeCompare(right.title, 'fr');
      });

    return { placements, candidates };
  }

  async placeOccurrence(
    occurrenceId: number,
    calendarDate: string,
    axisId: number,
    position: number
  ): Promise<number> {
    const { data, error } = await this.client.rpc('place_task_occurrence_in_calendar', {
      p_occurrence_id: occurrenceId,
      p_calendar_date: calendarDate,
      p_axis_id: axisId,
      p_position: position
    });
    if (error) throw error;
    return Number(data);
  }

  async rescheduleOccurrence(occurrenceId: number, scheduledFor: string | null): Promise<void> {
    const { error } = await this.client.rpc('reschedule_task_occurrence_with_calendar', {
      p_occurrence_id: occurrenceId,
      p_scheduled_for: scheduledFor
    });
    if (error) throw error;
  }

  async deplanOccurrence(occurrenceId: number): Promise<boolean> {
    const { data, error } = await this.client.rpc('deplan_task_occurrence_from_calendar', {
      p_occurrence_id: occurrenceId
    });
    if (error) throw error;
    return data === true;
  }

  async setPlacementPosition(entryId: number, position: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({ position })
      .eq('id', entryId)
      .eq('entry_kind', 'TASK')
      .is('deleted_at', null);
    if (error) throw error;
  }

  async completeRoutineOccurrence(occurrenceId: number): Promise<void> {
    await this.setRoutineOccurrenceStatus(occurrenceId, 'COMPLETED');
  }

  async setRoutineOccurrenceStatus(
    occurrenceId: number,
    status: 'COMPLETED' | 'NOT_RECORDED'
  ): Promise<void> {
    const { error } = await this.client.rpc('set_routine_occurrence_status', {
      p_occurrence_id: occurrenceId,
      p_status: status
    });
    if (error) throw error;
  }

  private async loadOccurrences(ids: number[]): Promise<OccurrenceRow[]> {
    if (!ids.length) return [];
    const { data, error } = await this.client
      .from('task_occurrences')
      .select(`
        id,
        task_definition_id,
        task_kind,
        scheduled_for,
        original_scheduled_for,
        rollover_days,
        status,
        completed_on,
        completed_at,
        closed_at,
        cancellation_reason
      `)
      .in('id', ids);
    if (error) throw error;
    return (data ?? []) as OccurrenceRow[];
  }

  private async loadDefinitions(ids: number[]): Promise<DefinitionRow[]> {
    if (!ids.length) return [];
    const { data, error } = await this.client
      .from('task_definitions')
      .select('id,title,description,priority,default_interval_days')
      .in('id', ids)
      .is('archived_at', null);
    if (error) throw error;
    return (data ?? []) as DefinitionRow[];
  }

  private mapPlacement(
    placement: PlacementRow,
    occurrence: OccurrenceRow,
    definition: DefinitionRow
  ): TaskCalendarPlacement {
    return {
      id: placement.id,
      taskOccurrenceId: occurrence.id,
      taskDefinitionId: occurrence.task_definition_id,
      taskKind: occurrence.task_kind,
      title: definition.title,
      description: definition.description,
      priority: definition.priority,
      calendarDate: placement.calendar_date,
      axisId: placement.axis_id,
      position: placement.position,
      scheduledFor: occurrence.scheduled_for,
      originalScheduledFor: occurrence.original_scheduled_for,
      rolloverDays: occurrence.rollover_days,
      status: occurrence.status,
      completedOn: occurrence.completed_on,
      completedAt: occurrence.completed_at,
      closedAt: occurrence.closed_at,
      cancellationReason: occurrence.cancellation_reason,
      intervalDays: definition.default_interval_days
    };
  }
}
